import { orderBy, uniq } from 'lodash';
import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import {
  composableFiltersState,
  MainOperatorEnum,
  selectMainOperator,
  SingleFilterClause,
  updateFilterClause,
} from 'store/composableFiltersSlice';
import { OperatorField } from '../OperatorField';
import { PropertyField } from '../PropertyField';
import { ValueField } from '../ValueField';
import { ClausePrefix } from './ClausePrefix';

interface NestedClauseProps {
  data: SingleFilterClause;
  parentIndex: number;
  childrenIndex: number;
  noPrefix?: boolean;
}

export const NestedClause = ({
  data,
  parentIndex,
  childrenIndex,
  noPrefix,
}: NestedClauseProps) => {
  const { data: rawFilters } = useAppSelector(composableFiltersState);

  const dispatch = useAppDispatch();

  const selectOptions = useMemo(() => {
    const selectedProperty = rawFilters.filters?.find(
      (item) => item.name === data?.property?.name
    );

    const propertyGroups = orderBy(
      uniq(rawFilters.filters?.map((property) => property.group))
    );

    const properties = propertyGroups.map((group) => {
      const filteredProperties = rawFilters.filters?.filter(
        (item) => item.group === group
      );

      return {
        label: group,
        options: filteredProperties?.map((item) => ({
          value: item.name,
          label: item.display,
        })),
      };
    });

    const operators = selectedProperty?.operators?.map((option) => ({
      value: option.name,
      label: option.display.toLowerCase(),
    }));

    const values = orderBy(
      selectedProperty?.values?.map((option) => ({
        value: option.name,
        label: option.display,
      })),
      ['label']
    );

    return {
      properties,
      operators,
      values,
      type: selectedProperty?.type,
    };
  }, [data.property, rawFilters.filters]);

  const handleUpdateValueField = (e: any) => {
    dispatch(
      updateFilterClause({
        index: childrenIndex,
        parentIndex,
        type: 'groupedFilterClause',
        data: {
          value: e,
        },
      })
    );
  };

  const handleUpdateOperatorField = (selectedOption: {
    value: string;
    label: string;
  }) => {
    dispatch(
      updateFilterClause({
        index: childrenIndex,
        parentIndex,
        type: 'groupedFilterClause',
        data: {
          operator: {
            name: String(selectedOption?.value),
            display: String(selectedOption?.label),
          },
        },
      })
    );
  };

  const handleUpdatePropertyField = (selectedOption: {
    value: string;
    label: string;
  }) => {
    dispatch(
      updateFilterClause({
        index: childrenIndex,
        parentIndex,
        type: 'groupedFilterClause',
        data: {
          property: {
            name: String(selectedOption?.value),
            display: String(selectedOption?.label),
          },
          operator: {
            name: '',
            display: '',
          },
          value: '',
        },
      })
    );
  };

  const handleSelectNestedOperator = (selectedOption: {
    value: string;
    label: string;
  }) => {
    dispatch(
      selectMainOperator({
        type: 'groupedFilterClause',
        value: selectedOption?.value as MainOperatorEnum,
        index: parentIndex,
      })
    );
  };

  return (
    <>
      <div className="flex items-center justify-end space-x-2">
        {noPrefix ? null : (
          <ClausePrefix
            index={childrenIndex}
            handleSelectOperator={handleSelectNestedOperator}
            parentIndex={parentIndex}
          />
        )}
        <PropertyField
          selectOptions={selectOptions}
          filterState={data}
          placement="group"
          handlePropertyFieldUpdate={handleUpdatePropertyField}
        />
      </div>
      <OperatorField
        selectOptions={selectOptions}
        filterState={data}
        handleUpdateOperatorField={handleUpdateOperatorField}
        placement="group"
      />
      <ValueField
        selectOptions={selectOptions}
        filterState={data}
        handleUpdateValueField={handleUpdateValueField}
        placement="group"
      />
    </>
  );
};
